import type { MediaItemInterface } from '@hypercodestudio/basler-components';

import {
  getMediaImagePreloadAttrs,
  getMediaPreviewPreloadHrefs,
  isProgressiveImageCandidate,
  withMediaPreviewImage,
  withMediaPreferredImageSrc
} from '~/utils/mediaImageSrc';
import { preloadMediaHighRes } from '~/utils/preloadImage';
import {
  contentMediaPreviewQuality,
  contentMediaPreviewWidth
} from '~/components/global/constants';

export type UseProgressiveMediaOptions = {
  previewWidth?: number;
  previewQuality?: number;
  marker?: string;
  /** Preload blur preview via `<link rel="preload">` (use sparingly, e.g. hero LCP). */
  preloadInHead?: boolean;
  /** Make the final high-resolution image discoverable in SSR HTML while still showing the preview first. */
  preloadHighResInHead?: boolean;
  preloadKey?: string;
};

/**
 * Shows a tiny blurred preview immediately and swaps to the full-resolution image
 * once it has been fetched and decoded on the client.
 */
export function useProgressiveMedia(
  baseMedia: MediaItemInterface | undefined,
  options: UseProgressiveMediaOptions = {}
) {
  const previewWidth = options.previewWidth ?? contentMediaPreviewWidth;
  const previewQuality = options.previewQuality ?? contentMediaPreviewQuality;
  const preloadInHead = options.preloadInHead ?? false;
  const preloadHighResInHead = options.preloadHighResInHead ?? false;
  const preloadKey = options.preloadKey ?? 'progressive-media-blur-preview';
  const canUseProgressive = isProgressiveImageCandidate(baseMedia);

  const isHighResReady = ref(false);
  let preloadGeneration = 0;

  function invalidatePreloads(): void {
    preloadGeneration += 1;
  }

  const previewPreloadHrefs = computed(() => {
    if (!preloadInHead || !canUseProgressive) {
      return undefined;
    }

    return getMediaPreviewPreloadHrefs(baseMedia, {
      width: previewWidth,
      quality: previewQuality,
      marker: options.marker
    });
  });

  if (preloadInHead) {
    useHead(() => ({
      link: previewPreloadHrefs.value
        ? [
            ...(previewPreloadHrefs.value.avif
              ? [
                  {
                    key: `${preloadKey}-avif`,
                    rel: 'preload' as const,
                    as: 'image' as const,
                    type: 'image/avif',
                    href: previewPreloadHrefs.value.avif,
                    fetchpriority: 'high' as const
                  }
                ]
              : []),
            {
              key: `${preloadKey}-webp`,
              rel: 'preload' as const,
              as: 'image' as const,
              type: 'image/webp',
              href: previewPreloadHrefs.value.webp,
              fetchpriority: 'high' as const
            }
          ]
        : []
    }));
  }

  const highResPreloadAttrs = computed(() => {
    if (!preloadHighResInHead || !canUseProgressive) {
      return undefined;
    }

    return getMediaImagePreloadAttrs(baseMedia, 'avif');
  });

  if (preloadHighResInHead) {
    useHead(() => ({
      link: highResPreloadAttrs.value
        ? [
            {
              key: `${preloadKey}-high-res`,
              rel: 'preload',
              as: 'image',
              fetchpriority: 'high',
              ...highResPreloadAttrs.value
            }
          ]
        : []
    }));
  }

  function preloadHighRes(): void {
    if (!canUseProgressive || !baseMedia) {
      return;
    }

    const generation = preloadGeneration;
    void preloadMediaHighRes(baseMedia).then((loaded) => {
      if (!loaded || generation !== preloadGeneration) {
        return;
      }
      isHighResReady.value = true;
    });
  }

  if (canUseProgressive) {
    onMounted(() => {
      preloadHighRes();
    });

    onUnmounted(() => {
      invalidatePreloads();
    });
  }

  const media = computed(() => {
    if (!baseMedia) {
      return undefined;
    }

    if (!canUseProgressive || isHighResReady.value) {
      if (canUseProgressive && isHighResReady.value && import.meta.client) {
        return withMediaPreferredImageSrc(baseMedia);
      }

      return baseMedia;
    }

    return (
      withMediaPreviewImage(baseMedia, {
        width: previewWidth,
        quality: previewQuality,
        marker: options.marker
      }) ?? baseMedia
    );
  });

  return { media };
}
